import { Box, Drawer, Loader, Text, UnstyledButton } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getRecentNotesForPicker } from "../../services/note";
import { formatDate } from "../../utils";
import useStyles from "./style";

const UsePreviousNoteDrawer = ({ opened, close, onUseNote, userId }) => {
  const { classes } = useStyles();
  const isMobileScreen = useMediaQuery("(max-width: 600px)");
  const { allPatients } = useSelector((state) => state?.patient);
  const { allStaff } = useSelector((state) => state?.staff);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!opened) return;
    setSearch("");
    setLoading(true);
    // `userId` (when opened from a client profile) scopes the list to that
    // client's own previous notes; omitted on the standalone Notes module.
    getRecentNotesForPicker(userId).then((data) => {
      setNotes(data || []);
      setLoading(false);
    });
  }, [opened, userId]);

  const getClientName = (id) => allPatients?.find((p) => p?.id === id)?.name || "";
  const getStaffName = (id) => {
    const s = allStaff?.find((x) => x?.id === id);
    return s?.name || `${s?.firstName || ""} ${s?.lastName || ""}`.trim() || "";
  };

  // Normalizes the raw note (same shape edit mode receives) into the display
  // fields the mockup's .prevrow shows — client(s)/staff/date/plan chip/note preview.
  const rows = useMemo(() => {
    return (notes || []).map((note) => {
      const type = note?.noteId?.name || "—";

      // Incident Report stores multiple clients/staff as arrays under the
      // SAME "Client Name"/"Staff Name" field keys (saved there directly by
      // onSave, not under incidentDetails) — every other note type stores a
      // single id there. toArray handles both shapes uniformly, no
      // per-type branching needed.
      const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
      const clientIds = toArray(
        note?.fields?.["Client Name"] || note?.fields?.["Patient Name"]
      );
      const staffIds = toArray(note?.fields?.["Staff Name"]);

      const clients = clientIds.map(getClientName).filter(Boolean);
      const staff = staffIds.map(getStaffName).filter(Boolean);

      const desc =
        note?.fields?.Description ||
        note?.descriptions?.[0]?.description ||
        note?.essentialSupport?.[0]?.description ||
        note?.goals?.[0]?.behaviorDescription ||
        "";

      // Every real, resolvable field shown as its own badge (mockup shows
      // multiple .pchip badges per row, not just one) — Progress Note gets
      // Outcome #/Title + Category; Essential Support gets Type + Outcome #
      // + Activities; Intervention gets Goal #. Each array can hold more
      // than one entry (multiple outcomes/supports/goals on one note), so
      // every entry gets its own set of badges, not just the first.
      const planChips = [];
      if (note?.descriptions?.length) {
        note.descriptions.forEach((d) => {
          // The top-level outcomeTitle/outcomeNumber on a description hold the
          // outcome's ID (set to vv.id on select), NOT the human-readable
          // number/title — those live under details. Read details first so we
          // show "Outcome #2 · Employment", not "Outcome #1784788167384 · …".
          const outcomeTitle = d?.details?.outcomeTitle;
          const outcomeNumber = d?.details?.outcomeNumber;
          const category = d?.details?.outcomeCategory || d?.outComeCategory;
          if (outcomeTitle || outcomeNumber) {
            planChips.push(`Outcome #${outcomeNumber || ""} · ${outcomeTitle || ""}`.trim());
          }
          if (category) planChips.push(`Category: ${category}`);
        });
      } else if (note?.essentialSupport?.length) {
        note.essentialSupport.forEach((e) => {
          if (e?.essentialSupportType) planChips.push(`Essential Support Type: ${e.essentialSupportType}`);
          if (e?.essentialNumber) planChips.push(`Outcome #${e.essentialNumber}`);
          const activityNames = (e?.activities || [])
            .map((a) => a?.supportActivity)
            .filter(Boolean);
          if (activityNames.length) {
            planChips.push(`Essential Support Activities: ${activityNames.join(", ")}`);
          }
        });
      } else if (note?.goals?.length) {
        note.goals.forEach((g) => {
          if (g?.goalNumber) planChips.push(`Goal #${g.goalNumber}`);
          if (g?.identifiedGoal) planChips.push(g.identifiedGoal);
          if (g?.startTimeGoal || g?.endTimeGoal) {
            planChips.push(
              g?.startTimeGoal && g?.endTimeGoal
                ? `${g.startTimeGoal} - ${g.endTimeGoal}`
                : g?.startTimeGoal || g?.endTimeGoal
            );
          }
        });
      }

      // A note with a handful of outcomes/supports/goals shouldn't blow up
      // the row — cap what's shown and fold the rest into a "+N more" chip.
      const CHIP_CAP = 6;
      const visibleChips = planChips.slice(0, CHIP_CAP);
      const hiddenCount = planChips.length - visibleChips.length;
      if (hiddenCount > 0) visibleChips.push(`+${hiddenCount} more`);

      return {
        note,
        id: note?._id || note?.id,
        type,
        date: formatDate(note?.fields?.Date),
        clients,
        staff,
        desc,
        planChips: visibleChips,
        searchBlob: `${type} ${clients.join(" ")} ${staff.join(" ")}`.toLowerCase(),
      };
    });
  }, [notes, allPatients, allStaff]);

  const filteredRows = search.trim()
    ? rows.filter((r) => r.searchBlob.includes(search.trim().toLowerCase()))
    : rows;

  return (
    <Drawer
      opened={opened}
      onClose={close}
      title="Use a Previous Note"
      position="right"
      size={isMobileScreen ? "100%" : 700}
      zIndex={1000}
      overlayProps={{ blur: 3 }}
      className={classes.mymodal}
    >
      <Box
        component="input"
        placeholder="Search by client, type, or staff…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className={classes.search}
      />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Loader color="yellow" />
        </Box>
      ) : filteredRows.length === 0 ? (
        <Text className={classes.emptyState}>No previous notes found.</Text>
      ) : (
        <Box className={classes.prevList}>
          {filteredRows.map((r) => (
            <Box key={r.id} className={classes.prevRow}>
              <Box className={classes.pmeta}>
                <Box className={classes.ptop}>
                  <Text className={classes.ptype}>{r.type}</Text>
                  <Text className={classes.pdate}>{r.date}</Text>
                </Box>
                <Box className={classes.pfacts}>
                  <Box className={classes.pfact}>
                    <Text className={classes.pk}>
                      {r.clients.length > 1 ? "Clients" : "Client"}
                    </Text>
                    <Text>{r.clients.join(", ") || "—"}</Text>
                  </Box>
                  <Box className={classes.pfact}>
                    <Text className={classes.pk}>Staff</Text>
                    <Text>{r.staff.join(", ") || "—"}</Text>
                  </Box>
                </Box>
                {r.planChips.length > 0 && (
                  <Box className={classes.pplan}>
                    {r.planChips.map((chip, i) => (
                      <Text key={i} className={classes.pchip}>
                        {chip}
                      </Text>
                    ))}
                  </Box>
                )}
                {r.desc && (
                  <Box className={classes.pnote}>
                    <Text className={classes.pnk}>Note</Text>
                    <Text className={classes.pnt}>{r.desc}</Text>
                  </Box>
                )}
              </Box>
              <UnstyledButton
                className={classes.useThisBtn}
                onClick={() => onUseNote(r.note)}
              >
                Use this
              </UnstyledButton>
            </Box>
          ))}
        </Box>
      )}
      <Box className={classes.footer}>
        <UnstyledButton className={classes.cancelBtn} onClick={close}>
          Cancel
        </UnstyledButton>
      </Box>
    </Drawer>
  );
};

export default UsePreviousNoteDrawer;
